import { useMemo, useState } from "react";
import { Loader2, ScrollText } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import { formatarQuantidade } from "@/lib/quantidade";
import { formatPriceCents } from "@/components/services/service-meta";
import { CatalogoBusca } from "@/components/catalogo/CatalogoBusca";

const SEM_CATEGORIA = "__sem__";

/**
 * Saldo e valor por material.
 *
 * Saldo negativo aparece em vermelho e não é erro do sistema: significa que saiu material que
 * nunca entrou, ou seja, que o inventário daquele item nunca foi feito. É informação útil — é a
 * lista do que contar primeiro.
 */
export function SaldosPorMaterial({ onExtrato, onInformar }: { onExtrato: (id: string) => void; onInformar: (id: string) => void }) {
  const [busca, setBusca] = useState("");
  const [apenasNegativos, setApenasNegativos] = useState(false);
  const query = trpc.estoque.saldos.useQuery({ busca: busca.trim() || undefined, apenasNegativos });

  const grupos = useMemo(() => {
    const mapa = new Map<string, { label: string; ordem: number; itens: any[] }>();
    for (const i of query.data?.itens ?? []) {
      const label = i.material.subcategory?.label ?? "Sem categoria";
      const chave = i.material.subcategory?.id ?? SEM_CATEGORIA;
      if (!mapa.has(chave)) mapa.set(chave, { label, ordem: i.material.subcategory ? 0 : 1, itens: [] });
      mapa.get(chave)!.itens.push(i);
    }
    return [...mapa.values()].sort((a, b) => a.ordem - b.ordem || a.label.localeCompare(b.label, "pt-BR"));
  }, [query.data]);

  if (query.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-brand-600" />
      </div>
    );
  }
  const dados = query.data;
  if (!dados) return null;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-[260px] flex-1">
          <CatalogoBusca
            valor={busca}
            onChange={setBusca}
            placeholder="Buscar insumo ou fabricante"
            achados={dados.itens.length}
          />
        </div>
        <label className="flex items-center gap-1.5 text-[11px] text-zinc-600 dark:text-muted-foreground">
          <input type="checkbox" checked={apenasNegativos} onChange={(e) => setApenasNegativos(e.target.checked)} />
          Só os negativos
        </label>
      </div>

      <div className="flex flex-wrap items-baseline justify-between gap-2 rounded-lg bg-zinc-50 px-3 py-2 dark:bg-muted/40">
        <span className="text-xs text-zinc-600 dark:text-muted-foreground">Capital parado em estoque</span>
        <span className="text-sm font-semibold tabular-nums text-zinc-900 dark:text-foreground">
          {formatPriceCents(dados.totalCents)}
        </span>
        {dados.semPreco > 0 && (
          <p className="w-full text-[10.5px] text-zinc-500 dark:text-muted-foreground">
            {dados.semPreco} {dados.semPreco === 1 ? "insumo está fora da conta" : "insumos estão fora da conta"} por não
            ter preço de compra cadastrado.
          </p>
        )}
      </div>

      {grupos.map((g) => (
        <div key={g.label} className="rounded-lg border border-zinc-100 p-3 dark:border-border/60">
          <p className="flex items-baseline gap-1.5 text-xs font-semibold text-zinc-700 dark:text-foreground/80">
            {g.label}
            <span className="text-[10px] font-normal tabular-nums text-zinc-400 dark:text-muted-foreground/70">
              {g.itens.length} {g.itens.length === 1 ? "insumo" : "insumos"}
            </span>
          </p>
          <table className="mt-1.5 w-full text-xs">
            <thead className="border-b border-zinc-100 text-[10px] uppercase tracking-wider text-zinc-500 dark:border-border dark:text-muted-foreground">
              <tr>
                <th className="px-2 py-1.5 text-left font-semibold">Insumo</th>
                <th className="w-24 px-2 py-1.5 text-right font-semibold">Saldo</th>
                <th className="w-20 px-2 py-1.5 text-right font-semibold">Mínimo</th>
                <th className="w-24 px-2 py-1.5 text-right font-semibold">Valor</th>
                <th className="w-16 px-2 py-1.5" />
              </tr>
            </thead>
            <tbody>
              {g.itens.map((i: any) => (
                <tr key={i.material.id} className="border-b border-zinc-100 last:border-b-0 dark:border-border/50">
                  <td className="px-2 py-1.5">
                    <span className="block truncate text-zinc-900 dark:text-foreground">{i.material.name}</span>
                    <span className="text-[10px] text-zinc-400 dark:text-muted-foreground/70">
                      {i.material.manufacturerName}
                      {i.nuncaMovimentado && " · nunca movimentado"}
                    </span>
                  </td>
                  <td
                    className={cn(
                      "px-2 py-1.5 text-right tabular-nums",
                      i.saldo < 0
                        ? "font-semibold text-red-600 dark:text-red-400"
                        : "text-zinc-800 dark:text-foreground/90",
                    )}
                  >
                    {formatarQuantidade(i.saldo)}
                    {i.material.unit && <span className="ml-0.5 text-zinc-400">{i.material.unit}</span>}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-zinc-400 dark:text-muted-foreground/70">
                    {i.material.minimumStock != null ? formatarQuantidade(i.material.minimumStock) : "—"}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-zinc-600 dark:text-muted-foreground">
                    {i.valorCents != null ? formatPriceCents(i.valorCents) : "—"}
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <span className="inline-flex items-center gap-0.5">
                      <button
                        type="button"
                        onClick={() => onInformar(i.material.id)}
                        title="Informar saldo contado"
                        aria-label={`Informar saldo de ${i.material.name}`}
                        className="rounded px-1 py-0.5 text-[10px] font-semibold text-zinc-400 hover:bg-brand-50 hover:text-brand-700 dark:hover:bg-primary/10 dark:hover:text-primary"
                      >
                        contar
                      </button>
                      <button
                        type="button"
                        onClick={() => onExtrato(i.material.id)}
                        title="Ver os lançamentos"
                        aria-label={`Extrato de ${i.material.name}`}
                        className="rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 dark:hover:bg-white/5"
                      >
                        <ScrollText className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {dados.itens.length === 0 && (
        <p className="py-8 text-center text-xs text-zinc-400 dark:text-muted-foreground">
          {apenasNegativos ? "Nenhum insumo com saldo negativo." : "Nenhum insumo com essa busca."}
        </p>
      )}
    </div>
  );
}
